import { AiModel } from '../../../models/ai-config.model';
import {
  AI_IMAGES_FEATURE,
  BusinessImageUsage,
  ImageGenerationRow,
  ImageMonthSummary,
  ImageQuality,
  ImageThumbnail
} from '../../../models/ai-images.model';
import { BusinessAddOn, Plan } from '../../../models/plan.model';
import { dayRangeUtc } from '../../agents/agents-format';

/**
 * A server time as a UTC instant: the account API (and the AI API passing its rows through) writes UTC times with no
 * offset, which the browser — and the DatePipe — would otherwise read as local time. One that carries an offset is kept.
 */
export function utcInstant(value: string | null | undefined): string | null {
  if (!value) return null;
  return /(Z|[+-]\d{2}:?\d{2})$/i.test(value) ? value : `${value}Z`;
}

/**
 * Pure helpers for the AI images staff screens (specs/agents CONTRACT-v1.4 §9): the business panel and the
 * platform page under AI › Images. Kept out of the components so the month roll-up and the add-on match are
 * pinned by specs without a TestBed.
 */

/** Console pill for a generation status: in flight purple, counted green, refused/failed red, released grey. */
export function generationStatusPill(status: string | null | undefined): string {
  switch (status) {
    case 'Completed':
      return 'pill pill-green';
    case 'Reserved':
      return 'pill pill-purple';
    case 'Refused':
    case 'Failed':
      return 'pill pill-red';
    default:
      return 'pill pill-grey';
  }
}

/** "Refused · people" when a code is recorded, else the status itself. */
export function generationStatusLabel(row: Pick<ImageGenerationRow, 'status' | 'refusalCode'>): string {
  const code = row.refusalCode?.trim();
  return code ? `${row.status} · ${code}` : `${row.status}`;
}

/** Images that counted against the allowance: only a Completed request counts, and only what came back. */
export function countedImages(row: Pick<ImageGenerationRow, 'status' | 'returned'>): number {
  return row.status === 'Completed' ? Math.max(0, row.returned ?? 0) : 0;
}

export function purposeLabel(purpose: string | null | undefined): string {
  switch (purpose) {
    case 'builder':
      return 'Builder';
    case 'agent-run':
      return 'Agent run';
    default:
      return purpose || '—';
  }
}

/** `pages[0].sections[1].settings.mediaId` → "page 1 · section 2"; anything else as stored. */
export function slotLabel(slotKey: string | null | undefined): string {
  if (!slotKey) return '';
  const m = /^pages\[(\d+)\]\.sections\[(\d+)\]/.exec(slotKey);
  return m ? `page ${+m[1] + 1} · section ${+m[2] + 1}` : slotKey;
}

/** The candidates a generation stored that have an http(s) URL the console can show. */
export function thumbnailsOf(row: Pick<ImageGenerationRow, 'thumbnails'>): ImageThumbnail[] {
  return (row.thumbnails ?? []).filter(t => !!t && typeof t.url === 'string' && /^https?:\/\//i.test(t.url));
}

/** The route's own cost figure, else the generations' costs summed (a provider refusal can still cost money). */
export function usageCostUsd(usage: BusinessImageUsage | null | undefined): number | null {
  if (!usage) return null;
  if (typeof usage.costUsd === 'number') return usage.costUsd;
  return (usage.generations ?? []).reduce((sum, g) => sum + (g.costUsd || 0), 0);
}

/** yyyy-MM-01 for a row: its `monthUtc`, else the UTC month it was reserved in. */
export function monthKeyOf(row: Pick<ImageGenerationRow, 'monthUtc' | 'reservedAt'>): string {
  const stored = /^(\d{4}-\d{2})/.exec(row.monthUtc ?? '');
  if (stored) return `${stored[1]}-01`;
  const at = new Date(utcInstant(row.reservedAt) ?? '');
  if (isNaN(at.getTime())) return 'unknown';
  return `${at.getUTCFullYear()}-${`${at.getUTCMonth() + 1}`.padStart(2, '0')}-01`;
}

/** "Oct 2026" for yyyy-MM-01. */
export function monthLabel(monthUtc: string): string {
  const m = /^(\d{4})-(\d{2})/.exec(monthUtc ?? '');
  if (!m) return monthUtc || '—';
  return new Date(Date.UTC(+m[1], +m[2] - 1, 1)).toLocaleString('en-GB', { month: 'short', year: 'numeric', timeZone: 'UTC' });
}

/**
 * Rolls the generation list up by UTC month, newest first (FR-47.2 platform-wide: images created, cost,
 * refusals). Images created are what counted: Σ `returned` of Completed rows. Cost is every row's
 * `costUsd`, refused and failed ones included, because the provider may charge for them.
 */
export function summariseByMonth(rows: ReadonlyArray<ImageGenerationRow>): ImageMonthSummary[] {
  const months = new Map<string, ImageMonthSummary & { ids: Set<string> }>();
  for (const row of rows ?? []) {
    const key = monthKeyOf(row);
    let month = months.get(key);
    if (!month) {
      month = { monthUtc: key, requests: 0, images: 0, costUsd: 0, refusals: {}, refused: 0, failed: 0, released: 0, businesses: 0, ids: new Set<string>() };
      months.set(key, month);
    }
    month.requests++;
    month.images += countedImages(row);
    month.costUsd += row.costUsd || 0;
    if (row.status === 'Refused') {
      month.refused++;
      const code = row.refusalCode?.trim() || 'unknown';
      month.refusals[code] = (month.refusals[code] ?? 0) + 1;
    } else if (row.status === 'Failed') {
      month.failed++;
    } else if (row.status === 'Released') {
      month.released++;
    }
    if (row.businessId) month.ids.add(row.businessId.toLowerCase());
  }
  return [...months.values()]
    .map(({ ids, ...m }) => ({ ...m, businesses: ids.size }))
    .sort((a, b) => b.monthUtc.localeCompare(a.monthUtc));
}

/** Refusals by code over several months. */
export function refusalTotals(months: ReadonlyArray<ImageMonthSummary>): Record<string, number> {
  const totals: Record<string, number> = {};
  for (const m of months) {
    for (const [code, n] of Object.entries(m.refusals)) totals[code] = (totals[code] ?? 0) + n;
  }
  return totals;
}

/** Whole UTC months as ISO instants: `2026-05` … `2026-10` → 2026-05-01T00:00Z … 2026-10-31T23:59:59.999Z. */
export function monthRangeUtc(fromMonth: string, toMonth: string): { from: string | null; to: string | null } {
  const month = /^(\d{4})-(\d{2})$/;
  const f = month.exec(fromMonth ?? '');
  const t = month.exec(toMonth ?? '');
  // The last day of the To month, then the agents' own day range for the instants.
  const lastDay = t ? new Date(Date.UTC(+t[1], +t[2], 0)).getUTCDate() : 0;
  return dayRangeUtc(f ? `${f[1]}-${f[2]}-01` : null, t ? `${t[1]}-${t[2]}-${`${lastDay}`.padStart(2, '0')}` : null);
}

/** yyyy-MM of a date, UTC, `back` months earlier. */
export function isoMonthUtc(d: Date, back = 0): string {
  const at = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() - back, 1));
  return `${at.getUTCFullYear()}-${`${at.getUTCMonth() + 1}`.padStart(2, '0')}`;
}

/** An add-on plan that grants the "AI Images" feature (an explicit IsIncluded = 0 row does not). */
export function isAiImagesPlan(plan: Plan | null | undefined): boolean {
  if (!plan || plan.planKind !== 'addon') return false;
  return (plan.planFeatures ?? []).some(
    f => (f.featureName ?? '').trim().toLowerCase() === AI_IMAGES_FEATURE.toLowerCase() && f.isIncluded !== false
  );
}

/**
 * The business owner's live AI images add-ons. A plan the console knows is matched by its feature; a live
 * add-on whose plan is not in the list (an inactive plan) falls back to the seeded plan name.
 */
export function aiImagesAddOns(addOns: ReadonlyArray<BusinessAddOn> | null | undefined, plans: ReadonlyArray<Plan> | null | undefined): BusinessAddOn[] {
  const byId = new Map((plans ?? []).map(p => [p.planID, p]));
  return (addOns ?? []).filter(a => {
    if (!a?.isActive) return false;
    const plan = byId.get(a.planId);
    return plan ? isAiImagesPlan(plan) : /^ai images\b/i.test(a.planName ?? '');
  });
}

/**
 * Image output tokens a GPT Image model reports for one image at the largest size, by quality — the AI API's own table
 * (Plus.Agents.Website `WebsiteImages.OutputTokensPerImage`), so the floor prices an image the way the call is billed.
 */
const OUTPUT_TOKENS_PER_IMAGE: Record<ImageQuality, number> = { low: 408, medium: 1584, high: 6240 };

/** The text tokens the request template sends at most (AI API `WebsiteImages.PromptTokensEstimate`). */
const PROMPT_TOKENS_PER_IMAGE = 250;

/**
 * What one image costs on `model` at `quality`, USD, priced as the AI API prices the call: image token prices when either is
 * set (the output tokens for the largest size, plus the prompt at the text input price, never below a price per image), else
 * the price per image. Null when the model is unpriced for images.
 */
export function imageCostUsd(model: AiModel, quality: ImageQuality = 'medium'): number | null {
  const perImage = typeof model.pricePerImage === 'number' && model.pricePerImage > 0 ? model.pricePerImage : 0;
  if (model.imageOutputPricePerMTok != null || model.imageInputPricePerMTok != null) {
    const tokens = OUTPUT_TOKENS_PER_IMAGE[quality] ?? OUTPUT_TOKENS_PER_IMAGE.medium;
    const cost = (tokens * (model.imageOutputPricePerMTok ?? 0) + PROMPT_TOKENS_PER_IMAGE * (model.inputPricePerMTok ?? 0)) / 1_000_000;
    const price = Math.max(cost, perImage);
    return price > 0 ? price : null;
  }
  return perImage > 0 ? perImage : null;
}

/** The dearest image among enabled image models at `quality`, USD (per image or by image tokens); null when none is priced. */
export function dearestImagePriceUsd(models: ReadonlyArray<AiModel> | null | undefined, quality: ImageQuality = 'medium'): number | null {
  const prices = (models ?? [])
    .filter(m => m.isEnabled && m.supportsImageGeneration)
    .map(m => imageCostUsd(m, quality))
    .filter((p): p is number => p !== null);
  return prices.length ? Math.max(...prices) : null;
}

/** D50: worst case = allowance × the dearest image; recommended price floor = 3 × that, before payment fees. */
export function priceFloorUsd(allowance: number, dearestPerImageUsd: number | null): { worstCaseUsd: number; floorUsd: number } | null {
  if (dearestPerImageUsd === null || !(allowance > 0)) return null;
  const worstCaseUsd = allowance * dearestPerImageUsd;
  return { worstCaseUsd, floorUsd: 3 * worstCaseUsd };
}

/** The longest brand term the settings panel accepts (a word or a short name, not a sentence). */
export const BRAND_TERM_MAX_LENGTH = 100;

/** The most brand terms the account API stores (`PUT api/admin/agents/images/settings` refuses more). */
export const BRAND_TERMS_MAX = 500;

/** A brand term as stored: trimmed, inner whitespace collapsed; '' when nothing is left. */
export function normaliseBrandTerm(raw: string | null | undefined): string {
  return (raw ?? '').replace(/\s+/g, ' ').trim();
}

/** The list with `term` added unless it is blank, too long or already there (case-insensitively). */
export function addBrandTerm(terms: ReadonlyArray<string>, raw: string): { terms: string[]; error: string | null } {
  const term = normaliseBrandTerm(raw);
  if (!term) return { terms: [...terms], error: 'Type a word or name first.' };
  if (term.length > BRAND_TERM_MAX_LENGTH) return { terms: [...terms], error: `Keep a term to ${BRAND_TERM_MAX_LENGTH} characters.` };
  if (terms.some(t => t.toLowerCase() === term.toLowerCase())) return { terms: [...terms], error: `"${term}" is already on the list.` };
  if (terms.length >= BRAND_TERMS_MAX) return { terms: [...terms], error: `The list holds at most ${BRAND_TERMS_MAX} terms.` };
  return { terms: [...terms, term], error: null };
}

/** The settings rule 1 ≤ default ≤ cap ≤ 8, as the message the panel shows; null when it holds. */
export function agentRunLimitsError(defaultMax: unknown, maxCap: unknown, limit: number): string | null {
  const d = Number(defaultMax);
  const c = Number(maxCap);
  if (defaultMax === null || defaultMax === '' || maxCap === null || maxCap === '' || !Number.isInteger(d) || !Number.isInteger(c)) {
    return 'Both numbers are whole images.';
  }
  if (d < 1) return 'The default is at least 1 image.';
  if (c > limit) return `The cap is at most ${limit} images.`;
  if (d > c) return 'The default cannot be above the cap.';
  return null;
}
